// ===================================
// CENTRALIZED ACCOUNTING MOCK DATA
// ===================================
// This file contains all mock data for the accounting module
// When replacing with actual data, use these interfaces as reference

// =============================================================================
// ORGANIZATION & BRANCH INTERFACES
// =============================================================================

export interface Organization {
  id: string;
  business_name: string;
  owner_name: string;
  owner_email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
  };
  settings: {
    timezone: string;
    language: string;
    currency: string;
    tax_settings: {
      default_tax_rate: number;
      tax_number: string;
    };
  };
  subscription: {
    plan: string;
    features: string[];
    expires_at: string;
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Branch {
  branch_id: string;
  organization_id: string;
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
  };
  store_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// CHART OF ACCOUNTS INTERFACES
// =============================================================================

export interface ChartOfAccount {
  id: string;
  accountCode: string;
  accountName: string;
  accountType: "Asset" | "Liability" | "Equity" | "Revenue" | "Expense";
  currency: string;
  openingBalance?: number;
  currentBalance?: number;
  parentAccountId?: string;
  isParent: boolean;
  allowDirectPosting: boolean;
  level: number;
  isActive?: boolean;
  children?: ChartOfAccount[];
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// JOURNAL ENTRY INTERFACES
// =============================================================================

export interface JournalEntry {
  id: string; // JE-YYYY-XXXX format
  entry_date: number; // Epoch milliseconds
  narration: string;
  source_module: "Manual" | "POS" | "Procurement" | "Payroll" | "System Setup";
  status: "Draft" | "Posted" | "Reversed";
  posted_by: string; // User ID
  branch_id?: string; // Branch ID for branch-specific entries, null for organization-level
  created_at: number; // Epoch milliseconds
  updated_at: number; // Epoch milliseconds
  journal_lines: JournalLine[];
  journal_tags?: JournalTag[];
}

export interface JournalLine {
  id: string;
  journal_entry_id: string;
  account_id: string;
  account_code: string; // For easier display
  account_name: string; // For easier display
  debit?: number; // Decimal(18,3) - nullable
  credit?: number; // Decimal(18,3) - nullable
  description?: string;
}

export interface JournalTag {
  id: string;
  journal_entry_id: string;
  tag_type: "order_id" | "payslip_id" | "invoice_id" | "voucher_id" | "custom";
  tag_value: string;
  description?: string;
}

// =============================================================================
// VOUCHER INTERFACES
// =============================================================================

export interface Voucher {
  id: string; // PV-YYYY-XXXX, RV-YYYY-XXXX, JV-YYYY-XXXX format
  voucher_type: "Payment" | "Receipt" | "Journal";
  voucher_date: number; // Epoch milliseconds
  narration: string;
  beneficiary?: string; // Vendor/Customer name or null for journal vouchers
  linked_entity?: string; // Invoice #, Order #, etc.
  payment_method?: "Cash" | "Bank Transfer" | "Cheque" | "Card" | "N/A";
  reference_number?: string; // Cheque number, transfer reference, etc.
  branch_id?: string; // Branch ID for branch-specific vouchers
  status: "Draft" | "Posted" | "Reversed";
  total_amount: number;
  posted_by: string; // User ID
  posted_at?: number; // Epoch milliseconds when posted
  reversed_by?: string; // Reference to reversing voucher ID
  journal_entry_id?: string; // Generated journal entry ID
  created_at: number; // Epoch milliseconds
  updated_at: number; // Epoch milliseconds
  voucher_lines: VoucherLine[];
  attachments?: VoucherAttachment[];
}

export interface VoucherLine {
  id: string;
  voucher_id: string;
  account_id: string;
  account_code: string; // For easier display
  account_name: string; // For easier display
  debit?: number; // Decimal(18,3) - nullable
  credit?: number; // Decimal(18,3) - nullable
  description?: string;
}

export interface VoucherAttachment {
  id: string;
  voucher_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  uploaded_by: string;
  uploaded_at: string;
}

// =============================================================================
// MOCK DATA - ORGANIZATION
// =============================================================================

export const mockOrganization: Organization = {
  id: "dea36caa-e645-45e6-8614-2fdf23d69466",
  business_name: "Bin Mahmoud",
  owner_name: "Ahmed Bin Mahmoud",
  owner_email: "ahmed@binmahmoud.com",
  phone: "+973 1234 5678",
  address: {
    street: "Building 123, Road 456",
    city: "Manama",
    state: "Capital Governorate",
    country: "Bahrain",
    postal_code: "12345",
  },
  settings: {
    timezone: "Asia/Bahrain",
    language: "en",
    currency: "BHD",
    tax_settings: {
      default_tax_rate: 10.0,
      tax_number: "123456789",
    },
  },
  subscription: {
    plan: "enterprise",
    features: [
      "multi_branch",
      "advanced_reporting",
      "api_access",
      "custom_integrations",
      "priority_support",
    ],
    expires_at: "2025-12-31T23:59:59Z",
  },
  is_active: true,
  created_at: "2023-01-15T10:30:00Z",
  updated_at: "2024-03-01T14:22:00Z",
};

// =============================================================================
// MOCK DATA - BRANCHES
// =============================================================================

export const mockBranches: Branch[] = [
  {
    branch_id: "branch-001",
    organization_id: "dea36caa-e645-45e6-8614-2fdf23d69466",
    name: "Sitra Branch",
    address: {
      street: "Shop 45, Sitra Mall",
      city: "Sitra",
      state: "Southern Governorate",
      country: "Bahrain",
      postal_code: "12346",
    },
    store_id: "store-sitra-001",
    created_by: "user-001",
    created_at: "2023-02-01T09:00:00Z",
    updated_at: "2024-01-15T11:30:00Z",
  },
  {
    branch_id: "branch-002",
    organization_id: "dea36caa-e645-45e6-8614-2fdf23d69466",
    name: "Manama Branch",
    address: {
      street: "Unit 12, Manama Souq",
      city: "Manama",
      state: "Capital Governorate",
      country: "Bahrain",
      postal_code: "12347",
    },
    store_id: "store-manama-001",
    created_by: "user-002",
    created_at: "2023-03-15T10:15:00Z",
    updated_at: "2024-02-20T16:45:00Z",
  },
  {
    branch_id: "branch-003",
    organization_id: "dea36caa-e645-45e6-8614-2fdf23d69466",
    name: "Riffa Branch",
    address: {
      street: "Building 789, Riffa Views",
      city: "Riffa",
      state: "Southern Governorate",
      country: "Bahrain",
      postal_code: "12348",
    },
    store_id: "store-riffa-001",
    created_by: "user-003",
    created_at: "2023-04-20T14:30:00Z",
    updated_at: "2024-01-10T09:20:00Z",
  },
  {
    branch_id: "branch-004",
    organization_id: "dea36caa-e645-45e6-8614-2fdf23d69466",
    name: "Muharraq Branch",
    address: {
      street: "Store 56, Muharraq Market",
      city: "Muharraq",
      state: "Muharraq Governorate",
      country: "Bahrain",
      postal_code: "12349",
    },
    store_id: "store-muharraq-001",
    created_by: "user-004",
    created_at: "2023-05-10T11:45:00Z",
    updated_at: "2024-03-05T13:15:00Z",
  },
  {
    branch_id: "branch-005",
    organization_id: "dea36caa-e645-45e6-8614-2fdf23d69466",
    name: "Isa Town Branch",
    address: {
      street: "Shop 23, Isa Town Center",
      city: "Isa Town",
      state: "Central Governorate",
      country: "Bahrain",
      postal_code: "12350",
    },
    store_id: "store-isatown-001",
    created_by: "user-005",
    created_at: "2023-06-25T08:30:00Z",
    updated_at: "2024-02-28T10:00:00Z",
  },
];

// =============================================================================
// MOCK DATA - CHART OF ACCOUNTS
// =============================================================================

// Organization-level COA (consolidated, higher balances)
const organizationCOA: ChartOfAccount[] = [
  // ASSETS - Level 0 Central Node
  {
    id: "100",
    accountCode: "100",
    accountName: "Assets",
    accountType: "Asset",
    currency: "BHD",
    parentAccountId: undefined,
    isParent: true,
    allowDirectPosting: false,
    level: 0,
    isActive: true,
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-03-15T10:30:00Z",
    children: [
      {
        id: "1000",
        accountCode: "1000",
        accountName: "Current Assets",
        accountType: "Asset",
        currency: "BHD",
        parentAccountId: "100",
        isParent: true,
        allowDirectPosting: false,
        level: 1,
        isActive: true,
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-03-15T10:30:00Z",
        children: [
          {
            id: "1001",
            accountCode: "1001",
            accountName: "Cash on Hand",
            accountType: "Asset",
            currency: "BHD",
            openingBalance: 25000.0,
            currentBalance: 28500.0,
            parentAccountId: "1000",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-18T14:15:00Z",
          },
          {
            id: "1002",
            accountCode: "1002",
            accountName: "Bank Account - Current",
            accountType: "Asset",
            currency: "BHD",
            openingBalance: 150000.0,
            currentBalance: 145000.0,
            parentAccountId: "1000",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-18T10:00:00Z",
          },
          {
            id: "1003",
            accountCode: "1003",
            accountName: "Credit Card Receivables",
            accountType: "Asset",
            currency: "BHD",
            openingBalance: 5000.0,
            currentBalance: 7500.0,
            parentAccountId: "1000",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-18T14:15:00Z",
          },
        ],
      },
      {
        id: "1300",
        accountCode: "1300",
        accountName: "Inventory",
        accountType: "Asset",
        currency: "BHD",
        parentAccountId: "100",
        isParent: true,
        allowDirectPosting: false,
        level: 1,
        isActive: true,
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-03-15T14:20:00Z",
        children: [
          {
            id: "1301",
            accountCode: "1301",
            accountName: "Food Inventory",
            accountType: "Asset",
            currency: "BHD",
            openingBalance: 45000.0,
            currentBalance: 48000.0,
            parentAccountId: "1300",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-15T14:20:00Z",
          },
        ],
      },
      {
        id: "1400",
        accountCode: "1400",
        accountName: "Fixed Assets",
        accountType: "Asset",
        currency: "BHD",
        parentAccountId: "100",
        isParent: true,
        allowDirectPosting: false,
        level: 1,
        isActive: true,
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-03-17T16:00:00Z",
        children: [
          {
            id: "1401",
            accountCode: "1401",
            accountName: "Accumulated Depreciation",
            accountType: "Asset",
            currency: "BHD",
            openingBalance: -10000.0,
            currentBalance: -12000.0,
            parentAccountId: "1400",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-17T16:00:00Z",
          },
        ],
      },
    ],
  },

  // LIABILITIES - Level 0 Central Node
  {
    id: "200",
    accountCode: "200",
    accountName: "Liabilities",
    accountType: "Liability",
    currency: "BHD",
    parentAccountId: undefined,
    isParent: true,
    allowDirectPosting: false,
    level: 0,
    isActive: true,
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-03-15T14:20:00Z",
    children: [
      {
        id: "2000",
        accountCode: "2000",
        accountName: "Current Liabilities",
        accountType: "Liability",
        currency: "BHD",
        parentAccountId: "200",
        isParent: true,
        allowDirectPosting: false,
        level: 1,
        isActive: true,
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-03-15T14:20:00Z",
        children: [
          {
            id: "2001",
            accountCode: "2001",
            accountName: "Accounts Payable",
            accountType: "Liability",
            currency: "BHD",
            openingBalance: 15000.0,
            currentBalance: 18000.0,
            parentAccountId: "2000",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-15T14:20:00Z",
          },
        ],
      },
    ],
  },

  // EQUITY - Level 0 Central Node
  {
    id: "300",
    accountCode: "300",
    accountName: "Equity",
    accountType: "Equity",
    currency: "BHD",
    parentAccountId: undefined,
    isParent: true,
    allowDirectPosting: false,
    level: 0,
    isActive: true,
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
    children: [
      {
        id: "3000",
        accountCode: "3000",
        accountName: "Owner's Equity",
        accountType: "Equity",
        currency: "BHD",
        openingBalance: 200000.0,
        currentBalance: 215000.0,
        parentAccountId: "300",
        isParent: false,
        allowDirectPosting: true,
        level: 1,
        isActive: true,
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-01-01T00:00:00Z",
      },
    ],
  },

  // REVENUE - Level 0 Central Node
  {
    id: "400",
    accountCode: "400",
    accountName: "Revenue",
    accountType: "Revenue",
    currency: "BHD",
    parentAccountId: undefined,
    isParent: true,
    allowDirectPosting: false,
    level: 0,
    isActive: true,
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-03-18T14:15:00Z",
    children: [
      {
        id: "4000",
        accountCode: "4000",
        accountName: "Sales Revenue",
        accountType: "Revenue",
        currency: "BHD",
        parentAccountId: "400",
        isParent: true,
        allowDirectPosting: false,
        level: 1,
        isActive: true,
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-03-18T14:15:00Z",
        children: [
          {
            id: "4001",
            accountCode: "4001",
            accountName: "Sales - Food",
            accountType: "Revenue",
            currency: "BHD",
            openingBalance: 0.0,
            currentBalance: 125000.0,
            parentAccountId: "4000",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-18T14:15:00Z",
          },
          {
            id: "4002",
            accountCode: "4002",
            accountName: "Sales Returns",
            accountType: "Revenue",
            currency: "BHD",
            openingBalance: 0.0,
            currentBalance: 2500.0,
            parentAccountId: "4000",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-17T13:30:00Z",
          },
        ],
      },
    ],
  },

  // EXPENSES - Level 0 Central Node
  {
    id: "500",
    accountCode: "500",
    accountName: "Expenses",
    accountType: "Expense",
    currency: "BHD",
    parentAccountId: undefined,
    isParent: true,
    allowDirectPosting: false,
    level: 0,
    isActive: true,
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-03-18T10:00:00Z",
    children: [
      {
        id: "5000",
        accountCode: "5000",
        accountName: "Operating Expenses",
        accountType: "Expense",
        currency: "BHD",
        parentAccountId: "500",
        isParent: true,
        allowDirectPosting: false,
        level: 1,
        isActive: true,
        createdAt: "2024-01-01T00:00:00Z",
        updatedAt: "2024-03-18T10:00:00Z",
        children: [
          {
            id: "5001",
            accountCode: "5001",
            accountName: "Salaries Expense",
            accountType: "Expense",
            currency: "BHD",
            openingBalance: 0.0,
            currentBalance: 25000.0,
            parentAccountId: "5000",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-16T09:15:00Z",
          },
          {
            id: "5101",
            accountCode: "5101",
            accountName: "Bank Charges",
            accountType: "Expense",
            currency: "BHD",
            openingBalance: 0.0,
            currentBalance: 800.0,
            parentAccountId: "5000",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-16T11:45:00Z",
          },
          {
            id: "5201",
            accountCode: "5201",
            accountName: "Depreciation Expense",
            accountType: "Expense",
            currency: "BHD",
            openingBalance: 0.0,
            currentBalance: 3000.0,
            parentAccountId: "5000",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-17T16:00:00Z",
          },
          {
            id: "5301",
            accountCode: "5301",
            accountName: "Utilities Expense",
            accountType: "Expense",
            currency: "BHD",
            openingBalance: 0.0,
            currentBalance: 1200.0,
            parentAccountId: "5000",
            isParent: false,
            allowDirectPosting: true,
            level: 2,
            isActive: true,
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-03-18T10:00:00Z",
          },
        ],
      },
    ],
  },
];

// Branch-specific COAs (smaller balances, location-specific accounts)
const branchCOAs: Record<string, ChartOfAccount[]> = {
  "branch-001": [
    // Sitra Branch COA - Assets Level 0 Central Node
    {
      id: "100-sitra",
      accountCode: "100",
      accountName: "Assets",
      accountType: "Asset",
      currency: "BHD",
      parentAccountId: undefined,
      isParent: true,
      allowDirectPosting: false,
      level: 0,
      isActive: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-03-15T10:30:00Z",
      children: [
        {
          id: "1000-sitra",
          accountCode: "1000",
          accountName: "Current Assets - Sitra",
          accountType: "Asset",
          currency: "BHD",
          parentAccountId: "100-sitra",
          isParent: true,
          allowDirectPosting: false,
          level: 1,
          isActive: true,
          createdAt: "2024-01-01T00:00:00Z",
          updatedAt: "2024-03-15T10:30:00Z",
          children: [
            {
              id: "1001-sitra",
              accountCode: "1001",
              accountName: "Cash on Hand - Sitra",
              accountType: "Asset",
              currency: "BHD",
              openingBalance: 3000.0,
              currentBalance: 3500.0,
              parentAccountId: "1000-sitra",
              isParent: false,
              allowDirectPosting: true,
              level: 2,
              isActive: true,
              createdAt: "2024-01-01T00:00:00Z",
              updatedAt: "2024-03-18T14:15:00Z",
            },
            {
              id: "1002-sitra",
              accountCode: "1002",
              accountName: "Bank Account - Sitra",
              accountType: "Asset",
              currency: "BHD",
              openingBalance: 15000.0,
              currentBalance: 18000.0,
              parentAccountId: "1000-sitra",
              isParent: false,
              allowDirectPosting: true,
              level: 2,
              isActive: true,
              createdAt: "2024-01-01T00:00:00Z",
              updatedAt: "2024-03-18T10:00:00Z",
            },
          ],
        },
      ],
    },
    // Revenue Level 0 Central Node
    {
      id: "400-sitra",
      accountCode: "400",
      accountName: "Revenue",
      accountType: "Revenue",
      currency: "BHD",
      parentAccountId: undefined,
      isParent: true,
      allowDirectPosting: false,
      level: 0,
      isActive: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-03-18T14:15:00Z",
      children: [
        {
          id: "4000-sitra",
          accountCode: "4000",
          accountName: "Sales Revenue - Sitra",
          accountType: "Revenue",
          currency: "BHD",
          parentAccountId: "400-sitra",
          isParent: true,
          allowDirectPosting: false,
          level: 1,
          isActive: true,
          createdAt: "2024-01-01T00:00:00Z",
          updatedAt: "2024-03-18T14:15:00Z",
          children: [
            {
              id: "4001-sitra",
              accountCode: "4001",
              accountName: "Food Sales - Sitra",
              accountType: "Revenue",
              currency: "BHD",
              openingBalance: 0.0,
              currentBalance: 15000.0,
              parentAccountId: "4000-sitra",
              isParent: false,
              allowDirectPosting: true,
              level: 2,
              isActive: true,
              createdAt: "2024-01-01T00:00:00Z",
              updatedAt: "2024-03-18T14:15:00Z",
            },
          ],
        },
      ],
    },
  ],
  // Similar structure for other branches (simplified - can be expanded as needed)
  "branch-002": [
    {
      id: "100-manama",
      accountCode: "100",
      accountName: "Assets",
      accountType: "Asset",
      currency: "BHD",
      parentAccountId: undefined,
      isParent: true,
      allowDirectPosting: false,
      level: 0,
      isActive: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-03-15T10:30:00Z",
      children: [],
    },
    {
      id: "400-manama",
      accountCode: "400",
      accountName: "Revenue",
      accountType: "Revenue",
      currency: "BHD",
      parentAccountId: undefined,
      isParent: true,
      allowDirectPosting: false,
      level: 0,
      isActive: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-03-18T14:15:00Z",
      children: [],
    },
  ],
  "branch-003": [
    {
      id: "100-riffa",
      accountCode: "100",
      accountName: "Assets",
      accountType: "Asset",
      currency: "BHD",
      parentAccountId: undefined,
      isParent: true,
      allowDirectPosting: false,
      level: 0,
      isActive: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-03-15T10:30:00Z",
      children: [],
    },
    {
      id: "400-riffa",
      accountCode: "400",
      accountName: "Revenue",
      accountType: "Revenue",
      currency: "BHD",
      parentAccountId: undefined,
      isParent: true,
      allowDirectPosting: false,
      level: 0,
      isActive: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-03-18T14:15:00Z",
      children: [],
    },
  ],
  "branch-004": [
    {
      id: "100-muharraq",
      accountCode: "100",
      accountName: "Assets",
      accountType: "Asset",
      currency: "BHD",
      parentAccountId: undefined,
      isParent: true,
      allowDirectPosting: false,
      level: 0,
      isActive: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-03-15T10:30:00Z",
      children: [],
    },
    {
      id: "400-muharraq",
      accountCode: "400",
      accountName: "Revenue",
      accountType: "Revenue",
      currency: "BHD",
      parentAccountId: undefined,
      isParent: true,
      allowDirectPosting: false,
      level: 0,
      isActive: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-03-18T14:15:00Z",
      children: [],
    },
  ],
  "branch-005": [
    {
      id: "100-isatown",
      accountCode: "100",
      accountName: "Assets",
      accountType: "Asset",
      currency: "BHD",
      parentAccountId: undefined,
      isParent: true,
      allowDirectPosting: false,
      level: 0,
      isActive: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-03-15T10:30:00Z",
      children: [],
    },
    {
      id: "400-isatown",
      accountCode: "400",
      accountName: "Revenue",
      accountType: "Revenue",
      currency: "BHD",
      parentAccountId: undefined,
      isParent: true,
      allowDirectPosting: false,
      level: 0,
      isActive: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-03-18T14:15:00Z",
      children: [],
    },
  ],
};

export const allCOAData = {
  organization: organizationCOA,
  branches: branchCOAs,
};

// =============================================================================
// MOCK DATA - JOURNAL ENTRIES
// =============================================================================

export const mockJournalEntries: JournalEntry[] = [
  {
    id: "JE-2024-0001",
    entry_date: 1710497400000, // 2024-03-15T10:30:00Z
    narration: "Customer payment for food order #12345",
    source_module: "POS",
    status: "Posted",
    posted_by: "user-001",
    branch_id: "branch-001",
    created_at: 1710497400000, // 2024-03-15T10:30:00Z
    updated_at: 1710497700000, // 2024-03-15T10:35:00Z
    journal_lines: [
      {
        id: "jl-001",
        journal_entry_id: "JE-2024-0001",
        account_id: "1001",
        account_code: "1001",
        account_name: "Cash on Hand",
        debit: 10.0,
        description: "Cash payment received",
      },
      {
        id: "jl-002",
        journal_entry_id: "JE-2024-0001",
        account_id: "4001",
        account_code: "4001",
        account_name: "Sales - Food",
        credit: 10.0,
        description: "Food sales revenue",
      },
    ],
    journal_tags: [
      {
        id: "jt-001",
        journal_entry_id: "JE-2024-0001",
        tag_type: "order_id",
        tag_value: "12345",
        description: "POS Order Reference",
      },
    ],
  },
  {
    id: "JE-2024-0002",
    entry_date: 1710511200000, // 2024-03-15T14:20:00Z
    narration: "Purchase of inventory items",
    source_module: "Procurement",
    status: "Posted",
    posted_by: "user-002",
    branch_id: "branch-002",
    created_at: 1710511200000, // 2024-03-15T14:20:00Z
    updated_at: 1710511500000, // 2024-03-15T14:25:00Z
    journal_lines: [
      {
        id: "jl-003",
        journal_entry_id: "JE-2024-0002",
        account_id: "1301",
        account_code: "1301",
        account_name: "Food Inventory",
        debit: 250.0,
        description: "Inventory purchase",
      },
      {
        id: "jl-004",
        journal_entry_id: "JE-2024-0002",
        account_id: "2001",
        account_code: "2001",
        account_name: "Accounts Payable",
        credit: 250.0,
        description: "Amount owed to supplier",
      },
    ],
    journal_tags: [
      {
        id: "jt-002",
        journal_entry_id: "JE-2024-0002",
        tag_type: "invoice_id",
        tag_value: "INV-2024-001",
        description: "Supplier Invoice Reference",
      },
    ],
  },
  {
    id: "JE-2024-0003",
    entry_date: 1710577700000, // 2024-03-16T09:15:00Z
    narration: "Employee salary payment",
    source_module: "Payroll",
    status: "Posted",
    posted_by: "user-003",
    branch_id: "branch-003",
    created_at: 1710577700000, // 2024-03-16T09:15:00Z
    updated_at: 1710578000000, // 2024-03-16T09:20:00Z
    journal_lines: [
      {
        id: "jl-005",
        journal_entry_id: "JE-2024-0003",
        account_id: "5001",
        account_code: "5001",
        account_name: "Salaries Expense",
        debit: 1500.0,
        description: "Monthly salary payment",
      },
      {
        id: "jl-006",
        journal_entry_id: "JE-2024-0003",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        credit: 1500.0,
        description: "Bank transfer for salary",
      },
    ],
    journal_tags: [
      {
        id: "jt-003",
        journal_entry_id: "JE-2024-0003",
        tag_type: "payslip_id",
        tag_value: "PAY-2024-003-001",
        description: "Employee Payslip Reference",
      },
    ],
  },
  {
    id: "JE-2024-0004",
    entry_date: 1710587100000, // 2024-03-16T11:45:00Z
    narration: "Bank charges for transaction fees",
    source_module: "Manual",
    status: "Posted",
    posted_by: "user-001",
    branch_id: undefined, // Organization-level entry
    created_at: 1710587100000, // 2024-03-16T11:45:00Z
    updated_at: 1710587400000, // 2024-03-16T11:50:00Z
    journal_lines: [
      {
        id: "jl-007",
        journal_entry_id: "JE-2024-0004",
        account_id: "5101",
        account_code: "5101",
        account_name: "Bank Charges",
        debit: 15.5,
        description: "Monthly bank service charges",
      },
      {
        id: "jl-008",
        journal_entry_id: "JE-2024-0004",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        credit: 15.5,
        description: "Bank charges deducted",
      },
    ],
  },
  {
    id: "JE-2024-0005",
    entry_date: 1710680200000, // 2024-03-17T13:30:00Z
    narration: "Customer refund for returned order",
    source_module: "POS",
    status: "Posted",
    posted_by: "user-002",
    branch_id: "branch-001",
    created_at: 1710680200000, // 2024-03-17T13:30:00Z
    updated_at: 1710680500000, // 2024-03-17T13:35:00Z
    journal_lines: [
      {
        id: "jl-009",
        journal_entry_id: "JE-2024-0005",
        account_id: "4002",
        account_code: "4002",
        account_name: "Sales Returns",
        debit: 25.0,
        description: "Food order return",
      },
      {
        id: "jl-010",
        journal_entry_id: "JE-2024-0005",
        account_id: "1001",
        account_code: "1001",
        account_name: "Cash on Hand",
        credit: 25.0,
        description: "Cash refund to customer",
      },
    ],
    journal_tags: [
      {
        id: "jt-004",
        journal_entry_id: "JE-2024-0005",
        tag_type: "order_id",
        tag_value: "12399",
        description: "Returned Order Reference",
      },
    ],
  },
  {
    id: "JE-2024-0006",
    entry_date: 1710689200000, // 2024-03-17T16:00:00Z
    narration: "Depreciation expense for equipment",
    source_module: "System Setup",
    status: "Posted",
    posted_by: "system",
    branch_id: undefined, // Organization-level entry
    created_at: 1710689200000, // 2024-03-17T16:00:00Z
    updated_at: 1710689200000, // 2024-03-17T16:00:00Z
    journal_lines: [
      {
        id: "jl-011",
        journal_entry_id: "JE-2024-0006",
        account_id: "5201",
        account_code: "5201",
        account_name: "Depreciation Expense",
        debit: 500.0,
        description: "Monthly depreciation",
      },
      {
        id: "jl-012",
        journal_entry_id: "JE-2024-0006",
        account_id: "1401",
        account_code: "1401",
        account_name: "Accumulated Depreciation",
        credit: 500.0,
        description: "Accumulated depreciation increase",
      },
    ],
  },
  {
    id: "JE-2024-0007",
    entry_date: 1710753600000, // 2024-03-18T10:00:00Z
    narration: "Utility bill payment",
    source_module: "Manual",
    status: "Draft",
    posted_by: "user-001",
    branch_id: "branch-002",
    created_at: 1710753600000, // 2024-03-18T10:00:00Z
    updated_at: 1710753600000, // 2024-03-18T10:00:00Z
    journal_lines: [
      {
        id: "jl-013",
        journal_entry_id: "JE-2024-0007",
        account_id: "5301",
        account_code: "5301",
        account_name: "Utilities Expense",
        debit: 120.0,
        description: "Electricity bill",
      },
      {
        id: "jl-014",
        journal_entry_id: "JE-2024-0007",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        credit: 120.0,
        description: "Payment for utilities",
      },
    ],
  },
  {
    id: "JE-2024-0008",
    entry_date: 1710769300000, // 2024-03-18T14:15:00Z
    narration: "Credit card payment processing",
    source_module: "POS",
    status: "Posted",
    posted_by: "user-002",
    branch_id: "branch-004",
    created_at: 1710769300000, // 2024-03-18T14:15:00Z
    updated_at: 1710769600000, // 2024-03-18T14:20:00Z
    journal_lines: [
      {
        id: "jl-015",
        journal_entry_id: "JE-2024-0008",
        account_id: "1003",
        account_code: "1003",
        account_name: "Credit Card Receivables",
        debit: 75.0,
        description: "Credit card sales",
      },
      {
        id: "jl-016",
        journal_entry_id: "JE-2024-0008",
        account_id: "4001",
        account_code: "4001",
        account_name: "Sales - Food",
        credit: 75.0,
        description: "Food sales via credit card",
      },
    ],
    journal_tags: [
      {
        id: "jt-005",
        journal_entry_id: "JE-2024-0008",
        tag_type: "order_id",
        tag_value: "12456",
        description: "Credit Card Order Reference",
      },
    ],
  },
  {
    id: "JE-2024-0009",
    entry_date: 1710835200000, // 2024-03-19T09:00:00Z
    narration: "Daily cash sales batch",
    source_module: "POS",
    status: "Posted",
    posted_by: "user-001",
    branch_id: "branch-005",
    created_at: 1710835200000, // 2024-03-19T09:00:00Z
    updated_at: 1710835500000, // 2024-03-19T09:05:00Z
    journal_lines: [
      {
        id: "jl-017",
        journal_entry_id: "JE-2024-0009",
        account_id: "1001",
        account_code: "1001",
        account_name: "Cash on Hand",
        debit: 320.0,
        description: "Daily cash collection",
      },
      {
        id: "jl-018",
        journal_entry_id: "JE-2024-0009",
        account_id: "4001",
        account_code: "4001",
        account_name: "Sales - Food",
        credit: 320.0,
        description: "Food sales revenue",
      },
    ],
    journal_tags: [
      {
        id: "jt-006",
        journal_entry_id: "JE-2024-0009",
        tag_type: "order_id",
        tag_value: "batch-19-001",
        description: "Daily Sales Batch",
      },
    ],
  },
  {
    id: "JE-2024-0010",
    entry_date: 1710862200000, // 2024-03-19T15:30:00Z
    narration: "Cash deposit to bank",
    source_module: "Manual",
    status: "Posted",
    posted_by: "user-003",
    branch_id: "branch-001",
    created_at: 1710862200000, // 2024-03-19T15:30:00Z
    updated_at: 1710862500000, // 2024-03-19T15:35:00Z
    journal_lines: [
      {
        id: "jl-019",
        journal_entry_id: "JE-2024-0010",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        debit: 250.0,
        description: "Cash deposited to bank",
      },
      {
        id: "jl-020",
        journal_entry_id: "JE-2024-0010",
        account_id: "1001",
        account_code: "1001",
        account_name: "Cash on Hand",
        credit: 250.0,
        description: "Cash transferred to bank",
      },
    ],
    journal_tags: [
      {
        id: "jt-007",
        journal_entry_id: "JE-2024-0010",
        tag_type: "voucher_id",
        tag_value: "DEP-001",
        description: "Deposit Voucher",
      },
    ],
  },
  {
    id: "JE-2024-0011",
    entry_date: 1710932400000, // 2024-03-20T11:00:00Z
    narration: "Supplier payment - monthly invoice",
    source_module: "Procurement",
    status: "Posted",
    posted_by: "user-002",
    branch_id: "branch-003",
    created_at: 1710932400000, // 2024-03-20T11:00:00Z
    updated_at: 1710932700000, // 2024-03-20T11:05:00Z
    journal_lines: [
      {
        id: "jl-021",
        journal_entry_id: "JE-2024-0011",
        account_id: "2001",
        account_code: "2001",
        account_name: "Accounts Payable",
        debit: 180.0,
        description: "Payment to supplier ABC Ltd",
      },
      {
        id: "jl-022",
        journal_entry_id: "JE-2024-0011",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        credit: 180.0,
        description: "Bank transfer payment",
      },
    ],
    journal_tags: [
      {
        id: "jt-008",
        journal_entry_id: "JE-2024-0011",
        tag_type: "invoice_id",
        tag_value: "INV-2024-002",
        description: "Supplier Invoice Payment",
      },
    ],
  },
  {
    id: "JE-2024-0014",
    entry_date: 1710374400000, // 2024-03-14T00:00:00Z
    narration: "Opening balance entry for new fiscal period",
    source_module: "System Setup",
    status: "Posted",
    posted_by: "system",
    branch_id: undefined, // Organization-level opening balances
    created_at: 1710374400000, // 2024-03-14T00:00:00Z
    updated_at: 1710374400000, // 2024-03-14T00:00:00Z
    journal_lines: [
      {
        id: "jl-027",
        journal_entry_id: "JE-2024-0014",
        account_id: "1001",
        account_code: "1001",
        account_name: "Cash on Hand",
        debit: 25000.0,
        description: "Opening balance - cash",
      },
      {
        id: "jl-028",
        journal_entry_id: "JE-2024-0014",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        debit: 150000.0,
        description: "Opening balance - bank",
      },
      {
        id: "jl-029",
        journal_entry_id: "JE-2024-0014",
        account_id: "1301",
        account_code: "1301",
        account_name: "Food Inventory",
        debit: 45000.0,
        description: "Opening balance - inventory",
      },
      {
        id: "jl-030",
        journal_entry_id: "JE-2024-0014",
        account_id: "2001",
        account_code: "2001",
        account_name: "Accounts Payable",
        credit: 15000.0,
        description: "Opening balance - payables",
      },
      {
        id: "jl-031",
        journal_entry_id: "JE-2024-0014",
        account_id: "3000",
        account_code: "3000",
        account_name: "Owner's Equity",
        credit: 205000.0,
        description: "Opening balance - equity",
      },
    ],
  },
  {
    id: "JE-2024-0015",
    entry_date: 1711108800000, // 2024-03-22T12:00:00Z
    narration: "Pending equipment purchase approval",
    source_module: "Manual",
    status: "Draft",
    posted_by: "user-002",
    branch_id: "branch-002",
    created_at: 1711108800000, // 2024-03-22T12:00:00Z
    updated_at: 1711108800000, // 2024-03-22T12:00:00Z
    journal_lines: [
      {
        id: "jl-032",
        journal_entry_id: "JE-2024-0015",
        account_id: "1400",
        account_code: "1400",
        account_name: "Fixed Assets",
        debit: 5000.0,
        description: "New equipment purchase",
      },
      {
        id: "jl-033",
        journal_entry_id: "JE-2024-0015",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        credit: 5000.0,
        description: "Payment for equipment",
      },
    ],
  },
];

// =============================================================================
// MOCK VOUCHER DATA
// =============================================================================

export const mockVouchers: Voucher[] = [
  {
    id: "PV-2024-0001",
    voucher_type: "Payment",
    voucher_date: 1710460800000, // 2024-03-15
    narration: "Payment to Al-Waha Food Suppliers for Invoice INV-0040",
    beneficiary: "Al-Waha Food Suppliers",
    linked_entity: "INV-0040",
    payment_method: "Bank Transfer",
    reference_number: "TXN-20240315-001",
    branch_id: "branch-003",
    status: "Posted",
    total_amount: 1200.0,
    posted_by: "user-002",
    posted_at: 1710497400000, // 2024-03-15T10:30:00Z
    journal_entry_id: "JE-2024-0011",
    created_at: 1710495600000, // 2024-03-15T10:00:00Z
    updated_at: 1710497400000, // 2024-03-15T10:30:00Z
    voucher_lines: [
      {
        id: "vl-001",
        voucher_id: "PV-2024-0001",
        account_id: "2001",
        account_code: "2001",
        account_name: "Accounts Payable",
        debit: 1200.0,
        description: "Payment to supplier",
      },
      {
        id: "vl-002",
        voucher_id: "PV-2024-0001",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        credit: 1200.0,
        description: "Bank transfer payment",
      },
    ],
    attachments: [
      {
        id: "att-001",
        voucher_id: "PV-2024-0001",
        file_name: "invoice_INV-0040.pdf",
        file_type: "application/pdf",
        file_size: 245760,
        file_url: "/uploads/vouchers/invoice_INV-0040.pdf",
        uploaded_by: "user-002",
        uploaded_at: "2024-03-15T10:15:00Z",
      },
    ],
  },
  {
    id: "RV-2024-0001",
    voucher_type: "Receipt",
    voucher_date: 1710547200000, // 2024-03-16
    narration: "Customer payment received for Order ORD-2024-0156",
    beneficiary: "Ahmed Al-Mansouri",
    linked_entity: "ORD-2024-0156",
    payment_method: "Cash",
    branch_id: "branch-001",
    status: "Posted",
    total_amount: 85.5,
    posted_by: "user-001",
    posted_at: 1710595200000, // 2024-03-16T14:20:00Z
    journal_entry_id: "JE-2024-0012",
    created_at: 1710594900000, // 2024-03-16T14:15:00Z
    updated_at: 1710595200000, // 2024-03-16T14:20:00Z
    voucher_lines: [
      {
        id: "vl-003",
        voucher_id: "RV-2024-0001",
        account_id: "1001",
        account_code: "1001",
        account_name: "Cash on Hand",
        debit: 85.5,
        description: "Cash received from customer",
      },
      {
        id: "vl-004",
        voucher_id: "RV-2024-0001",
        account_id: "1201",
        account_code: "1201",
        account_name: "Accounts Receivable",
        credit: 85.5,
        description: "Customer payment received",
      },
    ],
  },
  {
    id: "JV-2024-0001",
    voucher_type: "Journal",
    voucher_date: 1710633600000, // 2024-03-17
    narration: "Monthly depreciation adjustment for equipment",
    payment_method: "N/A",
    branch_id: "organization",
    status: "Posted",
    total_amount: 500.0,
    posted_by: "user-003",
    posted_at: 1710689200000, // 2024-03-17T16:00:00Z
    journal_entry_id: "JE-2024-0013",
    created_at: 1710688500000, // 2024-03-17T15:45:00Z
    updated_at: 1710689200000, // 2024-03-17T16:00:00Z
    voucher_lines: [
      {
        id: "vl-005",
        voucher_id: "JV-2024-0001",
        account_id: "5003",
        account_code: "5003",
        account_name: "Depreciation Expense",
        debit: 500.0,
        description: "Monthly depreciation charge",
      },
      {
        id: "vl-006",
        voucher_id: "JV-2024-0001",
        account_id: "1401",
        account_code: "1401",
        account_name: "Accumulated Depreciation",
        credit: 500.0,
        description: "Accumulated depreciation adjustment",
      },
    ],
  },
  {
    id: "PV-2024-0002",
    voucher_type: "Payment",
    voucher_date: 1710720000000, // 2024-03-18
    narration: "Utility bill payment - electricity",
    beneficiary: "Electricity & Water Authority",
    linked_entity: "BILL-EWA-202403",
    payment_method: "Cheque",
    reference_number: "CHQ-001234",
    branch_id: "branch-002",
    status: "Posted",
    total_amount: 320.0,
    posted_by: "user-002",
    posted_at: 1710757500000, // 2024-03-18T11:15:00Z
    journal_entry_id: "JE-2024-0014",
    created_at: 1710756600000, // 2024-03-18T11:00:00Z
    updated_at: 1710757500000, // 2024-03-18T11:15:00Z
    voucher_lines: [
      {
        id: "vl-007",
        voucher_id: "PV-2024-0002",
        account_id: "5002",
        account_code: "5002",
        account_name: "Utilities Expense",
        debit: 320.0,
        description: "Electricity bill payment",
      },
      {
        id: "vl-008",
        voucher_id: "PV-2024-0002",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        credit: 320.0,
        description: "Cheque payment",
      },
    ],
  },
  {
    id: "RV-2024-0002",
    voucher_type: "Receipt",
    voucher_date: 1710806400000, // 2024-03-19
    narration: "Advance payment from customer for catering order",
    beneficiary: "Manama Events Co.",
    linked_entity: "QUOTE-2024-0089",
    payment_method: "Bank Transfer",
    reference_number: "TXN-20240319-002",
    branch_id: "branch-002",
    status: "Posted",
    total_amount: 2500.0,
    posted_by: "user-001",
    posted_at: 1710836400000, // 2024-03-19T09:30:00Z
    journal_entry_id: "JE-2024-0015",
    created_at: 1710835500000, // 2024-03-19T09:15:00Z
    updated_at: 1710836400000, // 2024-03-19T09:30:00Z
    voucher_lines: [
      {
        id: "vl-009",
        voucher_id: "RV-2024-0002",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        debit: 2500.0,
        description: "Advance payment received",
      },
      {
        id: "vl-010",
        voucher_id: "RV-2024-0002",
        account_id: "2003",
        account_code: "2003",
        account_name: "Customer Advances",
        credit: 2500.0,
        description: "Customer advance liability",
      },
    ],
  },
  {
    id: "PV-2024-0003",
    voucher_type: "Payment",
    voucher_date: 1710892800000, // 2024-03-20
    narration: "Staff salary payment - March 2024",
    beneficiary: "Staff Payroll",
    linked_entity: "PAYROLL-202403",
    payment_method: "Bank Transfer",
    reference_number: "BULK-TXN-20240320",
    branch_id: "organization",
    status: "Posted",
    total_amount: 4500.0,
    posted_by: "user-003",
    posted_at: 1710946800000, // 2024-03-20T15:00:00Z
    journal_entry_id: "JE-2024-0016",
    created_at: 1710945000000, // 2024-03-20T14:30:00Z
    updated_at: 1710946800000, // 2024-03-20T15:00:00Z
    voucher_lines: [
      {
        id: "vl-011",
        voucher_id: "PV-2024-0003",
        account_id: "5001",
        account_code: "5001",
        account_name: "Salaries & Wages",
        debit: 4500.0,
        description: "Monthly staff salaries",
      },
      {
        id: "vl-012",
        voucher_id: "PV-2024-0003",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        credit: 4500.0,
        description: "Salary payments via bank transfer",
      },
    ],
  },
  {
    id: "RV-2024-0003",
    voucher_type: "Receipt",
    voucher_date: 1710979200000, // 2024-03-21
    narration: "Cash sales collection - daily batch",
    payment_method: "Cash",
    branch_id: "branch-001",
    status: "Posted",
    total_amount: 680.0,
    posted_by: "user-001",
    posted_at: 1711040400000, // 2024-03-21T18:00:00Z
    journal_entry_id: "JE-2024-0017",
    created_at: 1711039500000, // 2024-03-21T17:45:00Z
    updated_at: 1711040400000, // 2024-03-21T18:00:00Z
    voucher_lines: [
      {
        id: "vl-013",
        voucher_id: "RV-2024-0003",
        account_id: "1001",
        account_code: "1001",
        account_name: "Cash on Hand",
        debit: 680.0,
        description: "Daily cash sales collection",
      },
      {
        id: "vl-014",
        voucher_id: "RV-2024-0003",
        account_id: "4001",
        account_code: "4001",
        account_name: "Sales - Food",
        credit: 680.0,
        description: "Food sales revenue",
      },
    ],
  },
  {
    id: "PV-2024-0004",
    voucher_type: "Payment",
    voucher_date: 1711065600000, // 2024-03-22
    narration: "Rent payment for Sitra branch - March 2024",
    beneficiary: "Sitra Mall Management",
    linked_entity: "LEASE-SITRA-001",
    payment_method: "Bank Transfer",
    reference_number: "TXN-20240322-001",
    branch_id: "branch-001",
    status: "Draft",
    total_amount: 1800.0,
    posted_by: "user-002",
    created_at: 1711101600000, // 2024-03-22T10:00:00Z
    updated_at: 1711101600000, // 2024-03-22T10:00:00Z
    voucher_lines: [
      {
        id: "vl-015",
        voucher_id: "PV-2024-0004",
        account_id: "5004",
        account_code: "5004",
        account_name: "Rent Expense",
        debit: 1800.0,
        description: "Monthly rent - Sitra branch",
      },
      {
        id: "vl-016",
        voucher_id: "PV-2024-0004",
        account_id: "1002",
        account_code: "1002",
        account_name: "Bank Account - Current",
        credit: 1800.0,
        description: "Rent payment via bank transfer",
      },
    ],
  },
  {
    id: "JV-2024-0002",
    voucher_type: "Journal",
    voucher_date: 1711152000000, // 2024-03-23
    narration: "Inventory adjustment - stock count variance",
    payment_method: "N/A",
    branch_id: "branch-002",
    status: "Draft",
    total_amount: 150.0,
    posted_by: "user-003",
    created_at: 1711195200000, // 2024-03-23T14:00:00Z
    updated_at: 1711195200000, // 2024-03-23T14:00:00Z
    voucher_lines: [
      {
        id: "vl-017",
        voucher_id: "JV-2024-0002",
        account_id: "5005",
        account_code: "5005",
        account_name: "Inventory Adjustment",
        debit: 150.0,
        description: "Stock count variance - shortage",
      },
      {
        id: "vl-018",
        voucher_id: "JV-2024-0002",
        account_id: "1301",
        account_code: "1301",
        account_name: "Food Inventory",
        credit: 150.0,
        description: "Inventory reduction",
      },
    ],
  },
];

// =============================================================================
// ACCOUNTING CONFIGURATION INTERFACES
// =============================================================================

export interface COAMapping {
  id: string;
  module: "POS" | "HR" | "Inventory" | "Vouchers" | "Manual";
  transaction_type: string;
  voucher_type?: "Payment" | "Receipt" | "Journal";
  debit_account_id: string;
  credit_account_id: string;
  branch_id?: string;
  payment_method?: string;
  description: string;
  is_active: boolean;
  created_at: number;
  updated_at: number;
}

export interface VoucherSettings {
  id: string;
  // Section 1: Enabled Voucher Types
  enabled_voucher_types: {
    payment: boolean;
    receipt: boolean;
    journal: boolean;
    contra: boolean;
    opening: boolean;
  };
  // Section 2: Voucher Numbering Format
  voucher_type_prefixes: {
    payment: string;
    receipt: string;
    journal: string;
    contra: string;
    opening: string;
  };
  separate_sequence_per_branch: boolean;
  // Section 3: Allowed Payment Methods
  allowed_payment_methods: string[];
  payment_method_settings: {
    enable_cash: boolean;
    enable_bank_transfer: boolean;
    enable_qr_payment: boolean;
    enable_cheque: boolean;
    enable_split_payments: boolean;
    default_payment_method: string;
  };
  // Section 4: Field Requirements & Behavior
  field_requirements: {
    require_narration: boolean;
    require_reference_linking: boolean;
    require_attachment_upload: boolean;
    allow_line_level_narration: boolean;
    enable_multiline_vouchers: boolean;
    enable_voucher_draft_mode: boolean;
  };
  attachment_required: {
    payment: boolean;
    receipt: boolean;
    journal: boolean;
    contra: boolean;
    opening: boolean;
  };
  // Section 5: Default Narration Templates
  default_narration_templates: {
    payment: string[];
    receipt: string[];
    journal: string[];
    contra: string[];
    opening: string[];
  };
  allowed_posting_roles: string[];
  created_at: number;
  updated_at: number;
}

export interface JournalSettings {
  id: string;
  // 1. Journal Entry Workflow Rules
  enable_draft_journals: boolean;
  allow_manual_journals: boolean;
  allow_edits_after_save: boolean;
  auto_lock_on_post: boolean;
  allow_reversal_journals: boolean;
  restrict_partial_entries: boolean;
  restrict_save_without_narration: boolean;

  // 2. Journal Entry Numbering Format
  journal_id_prefix: string;
  sequence_reset_annually: boolean;
  branch_based_sequence: boolean;

  // 3. Posting Permissions & Role Control
  roles_allowed_to_create: string[];
  roles_allowed_to_post: string[];
  roles_allowed_to_reverse: string[];
  require_approval_before_posting: boolean;

  // 4. Audit & Logging Settings
  enable_full_audit_trail: boolean;
  store_original_entry_snapshot: boolean;
  log_coa_account_hits: boolean;
  audit_trail_logging: boolean;

  // 5. Validation Rules & Safeguards
  enforce_coa_postability: boolean;
  enforce_account_type_match: boolean;
  allow_zero_amount_lines: boolean;
  prevent_duplicate_entries: boolean;

  // 6. Advanced Features (Optional)
  enable_cost_center_tracking: boolean;
  enable_departmental_segmenting: boolean;
  enable_subledger_source_tagging: boolean;
  default_currency: string;

  // Legacy fields
  require_narration: boolean;
  minimum_journal_lines: number;
  created_at: number;
  updated_at: number;
}

export interface LedgerSettings {
  id: string;

  // 1. Account Visibility Rules
  show_only_postable_accounts: boolean;
  hide_zero_balance_accounts: boolean;
  show_archived_accounts: boolean;
  account_display_format: "[Code] - [Name]" | "[Name] only" | "[Name] (Code)";

  // 2. Balance Calculation Settings
  enable_opening_balances: boolean;
  balance_carry_forward_across_years: boolean;
  auto_close_nominal_accounts: boolean;
  currency_handling_mode:
    | "base_currency_only"
    | "multi_currency_with_conversion";
  include_draft_journals_in_ledger: boolean;

  // 3. Filters & User Access Controls
  allow_filtering_by_branch: boolean;
  allow_filtering_by_department: boolean;
  role_based_ledger_access: string[];
  role_based_account_group_visibility: Record<string, string[]>;

  // 4. Drill-Down Behavior
  enable_journal_drill_down: boolean;
  enable_source_module_tags: boolean;
  enable_view_source_document_link: boolean;

  // 5. Export & Reporting Controls
  allow_export_to_excel: boolean;
  allow_export_to_pdf: boolean;
  allow_export_to_csv: boolean;
  show_report_footer_summary: boolean;
  include_applied_filters_in_export: boolean;

  // Legacy fields for backward compatibility
  enable_branch_filter: boolean;
  show_reversed_entries_default: boolean;
  default_date_range:
    | "this_month"
    | "last_30_days"
    | "this_quarter"
    | "this_year";
  export_options_allowed: {
    pdf: boolean;
    excel: boolean;
    csv: boolean;
  };
  show_opening_balance_row: boolean;

  created_at: number;
  updated_at: number;
}

export interface UserRole {
  id: string;
  name: string;
  permissions: {
    journals: {
      create: boolean;
      edit: boolean;
      delete: boolean;
      post: boolean;
      reverse: boolean;
      view: boolean;
    };
    vouchers: {
      create: boolean;
      edit: boolean;
      delete: boolean;
      post: boolean;
      reverse: boolean;
      view: boolean;
    };
    coa: {
      create: boolean;
      edit: boolean;
      delete: boolean;
      view: boolean;
    };
    ledger: {
      view: boolean;
      export: boolean;
    };
    configuration: {
      view: boolean;
      edit: boolean;
    };
  };
  created_at: number;
  updated_at: number;
}

export interface AccountingConfiguration {
  coa_mappings: COAMapping[];
  voucher_settings: VoucherSettings;
  journal_settings: JournalSettings;
  ledger_settings: LedgerSettings;
  user_roles: UserRole[];
}

// =============================================================================
// MOCK CONFIGURATION DATA
// =============================================================================

export const mockCOAMappings: COAMapping[] = [
  // ===== POS MODULE MAPPINGS =====
  // A. Order Finalization (Sales)
  {
    id: "pos-001",
    module: "POS",
    transaction_type: "POS_SALE_CASH",
    debit_account_id: "1001", // Cash on Hand
    credit_account_id: "4001", // Sales Revenue
    description: "Cash sale - finalize with cash payment",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "pos-002",
    module: "POS",
    transaction_type: "POS_SALE_CARD",
    debit_account_id: "1003", // Bank (Card Receipts)
    credit_account_id: "4001", // Sales Revenue
    description: "Card sale - finalize with card payment",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "pos-003",
    module: "POS",
    transaction_type: "POS_SALE_QR",
    debit_account_id: "1002", // Bank (QR Receipts)
    credit_account_id: "4001", // Sales Revenue
    description: "QR payment sale - finalize with QR payment",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "pos-004",
    module: "POS",
    transaction_type: "POS_SALE_SPLIT",
    debit_account_id: "1001", // Cash/Bank (split handling)
    credit_account_id: "4001", // Sales Revenue
    description: "Split payment sale - multiple payment methods",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // B. Discount Handling
  {
    id: "pos-005",
    module: "POS",
    transaction_type: "POS_DISCOUNT",
    debit_account_id: "5401", // Discount Expense
    credit_account_id: "4001", // Sales Revenue
    description: "Manager-approved discount application",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // C. Tips Handling
  {
    id: "pos-006",
    module: "POS",
    transaction_type: "POS_TIP_COLLECTED",
    debit_account_id: "1001", // Cash/Bank
    credit_account_id: "2101", // Tips Payable
    description: "Tip collected during billing",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "pos-007",
    module: "POS",
    transaction_type: "POS_TIP_DISBURSED",
    debit_account_id: "2101", // Tips Payable
    credit_account_id: "1001", // Cash or Salary Payable
    description: "Tip paid out to staff",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // D. Refunds
  {
    id: "pos-008",
    module: "POS",
    transaction_type: "POS_REFUND_CASH",
    debit_account_id: "5501", // Refund Expense
    credit_account_id: "1001", // Cash on Hand
    description: "Cash refund to customer",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "pos-009",
    module: "POS",
    transaction_type: "POS_REFUND_ELECTRONIC",
    debit_account_id: "5501", // Refund Expense
    credit_account_id: "1002", // Bank
    description: "Electronic refund (card/QR) to customer",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // E. End-of-Shift Cash Reconciliation
  {
    id: "pos-010",
    module: "POS",
    transaction_type: "POS_CASH_OVERAGE",
    debit_account_id: "1001", // Cash on Hand
    credit_account_id: "4901", // Cash Overage Income
    description: "Cash surplus detected at shift closure",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "pos-011",
    module: "POS",
    transaction_type: "POS_CASH_SHORTAGE",
    debit_account_id: "5601", // Cash Shortage Expense
    credit_account_id: "1001", // Cash on Hand
    description: "Cash deficit detected at shift closure",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },

  // ===== HR MODULE MAPPINGS =====
  // A. Payroll Finalization (Bank Paid)
  {
    id: "hr-001",
    module: "HR",
    transaction_type: "HR_SALARY_BANK",
    debit_account_id: "5001", // Salary Expense
    credit_account_id: "1002", // Bank Account
    description: "Salary finalized and paid via bank",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // B. Payroll Finalization (Unpaid / Payable)
  {
    id: "hr-002",
    module: "HR",
    transaction_type: "HR_SALARY_PAYABLE",
    debit_account_id: "5001", // Salary Expense
    credit_account_id: "2201", // Salary Payable
    description: "Salary posted but not yet paid",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // C. Salary Disbursement (from Payable)
  {
    id: "hr-003",
    module: "HR",
    transaction_type: "HR_SALARY_SETTLEMENT",
    debit_account_id: "2201", // Salary Payable
    credit_account_id: "1002", // Bank/Cash
    description: "Salary paid from payable",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // D. Overtime Payment
  {
    id: "hr-004",
    module: "HR",
    transaction_type: "HR_OVERTIME",
    debit_account_id: "5002", // OT Expense
    credit_account_id: "2201", // Salary Payable/Bank
    description: "Overtime payment added to payslip",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // E. Tips Distribution
  {
    id: "hr-005",
    module: "HR",
    transaction_type: "HR_TIPS_DISTRIBUTED",
    debit_account_id: "2101", // Tips Payable
    credit_account_id: "1002", // Bank/Salary Payable
    description: "Tips distributed via payroll",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // F. Bonus or One-Off Allowance
  {
    id: "hr-006",
    module: "HR",
    transaction_type: "HR_BONUS",
    debit_account_id: "5003", // Bonus Expense
    credit_account_id: "2201", // Salary Payable/Bank
    description: "Bonus posted on payslip",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // G. Employee Advance Issued
  {
    id: "hr-007",
    module: "HR",
    transaction_type: "HR_SALARY_ADVANCE",
    debit_account_id: "1501", // Advance to Employees
    credit_account_id: "1002", // Bank/Cash
    description: "Advance issued to employee",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // H. Advance Recovery in Payslip
  {
    id: "hr-008",
    module: "HR",
    transaction_type: "HR_ADVANCE_RECOVERY",
    debit_account_id: "2201", // Salary Payable
    credit_account_id: "1501", // Advance to Employees
    description: "Advance deducted from salary",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // I. Loan Deduction
  {
    id: "hr-009",
    module: "HR",
    transaction_type: "HR_LOAN_RECOVERY",
    debit_account_id: "2201", // Salary Payable
    credit_account_id: "1502", // Employee Loan Receivable
    description: "Loan recovery from net pay",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // J. Payroll Reversal
  {
    id: "hr-010",
    module: "HR",
    transaction_type: "HR_PAYROLL_REVERSAL",
    debit_account_id: "1002", // All original credits → debit
    credit_account_id: "5001", // All original debits → credit
    description: "Payslip reversal - reverses original entry",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },

  // ===== INVENTORY MODULE MAPPINGS =====
  // A. Purchase Invoice Posting
  {
    id: "inv-001",
    module: "Inventory",
    transaction_type: "PROC_PURCHASE_INVENTORY",
    debit_account_id: "1301", // Inventory (Raw Material/Packaging)
    credit_account_id: "2001", // Accounts Payable (Vendor)
    description: "Inventory purchase - stock item invoice post-GRN",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "inv-002",
    module: "Inventory",
    transaction_type: "PROC_PURCHASE_EXPENSE",
    debit_account_id: "5701", // Expense Account (Cleaning Supplies)
    credit_account_id: "2001", // Accounts Payable
    description: "Non-stock item purchase (e.g., detergent)",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // B. Purchase VAT Handling
  {
    id: "inv-003",
    module: "Inventory",
    transaction_type: "PROC_PURCHASE_VAT",
    debit_account_id: "1601", // VAT Input (Receivable)
    credit_account_id: "2001", // Accounts Payable
    description: "VAT on purchase invoices",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // C. GRN Inventory Addition
  {
    id: "inv-004",
    module: "Inventory",
    transaction_type: "INV_GRN_TEMP_STOCK",
    debit_account_id: "1302", // Inventory (Temp Holding)
    credit_account_id: "2301", // Goods Received Not Invoiced (GRNI)
    description: "Stock added before invoice - GRN approved",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // D. Recipe-Based Consumption
  {
    id: "inv-005",
    module: "Inventory",
    transaction_type: "INV_RECIPE_CONSUMPTION",
    debit_account_id: "5801", // Cost of Goods Sold (COGS)
    credit_account_id: "1301", // Inventory (Ingredients)
    description: "Ingredients consumed on POS sale completion",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // E. Stock Transfer (Inter-Branch)
  {
    id: "inv-006",
    module: "Inventory",
    transaction_type: "INV_TRANSFER_BRANCH",
    debit_account_id: "1303", // Inventory (Receiving Branch)
    credit_account_id: "1301", // Inventory (Sending Branch)
    description: "Warehouse to branch transfer",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // F. Internal Transfer
  {
    id: "inv-007",
    module: "Inventory",
    transaction_type: "INV_TRANSFER_INTERNAL",
    debit_account_id: "1304", // Inventory (Target Zone)
    credit_account_id: "1301", // Inventory (Source Zone)
    description: "Storage to kitchen internal transfer",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // G. Inventory Wastage
  {
    id: "inv-008",
    module: "Inventory",
    transaction_type: "INV_WASTAGE_LOSS",
    debit_account_id: "5901", // Wastage Expense
    credit_account_id: "1301", // Inventory
    description: "Spoiled or broken stock wastage",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // H. Stock Adjustment
  {
    id: "inv-009",
    module: "Inventory",
    transaction_type: "INV_ADJUSTMENT_GAIN",
    debit_account_id: "1301", // Inventory
    credit_account_id: "4801", // Inventory Gain Income
    description: "Physical count higher than system",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "inv-010",
    module: "Inventory",
    transaction_type: "INV_ADJUSTMENT_LOSS",
    debit_account_id: "5902", // Inventory Loss Expense
    credit_account_id: "1301", // Inventory
    description: "Physical count lower than system",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  // I. Opening Inventory
  {
    id: "inv-011",
    module: "Inventory",
    transaction_type: "INV_OPENING_BALANCE",
    debit_account_id: "1301", // Inventory
    credit_account_id: "3000", // Owner's Equity/Suspense
    description: "Initial stock entry during system setup",
    is_active: true,
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
];

export const mockVoucherSettings: VoucherSettings = {
  id: "voucher-settings-001",
  // Section 1: Enabled Voucher Types
  enabled_voucher_types: {
    payment: true,
    receipt: true,
    journal: true,
    contra: false,
    opening: false,
  },
  // Section 2: Voucher Numbering Format
  voucher_type_prefixes: {
    payment: "PV-",
    receipt: "RV-",
    journal: "JV-",
    contra: "CV-",
    opening: "OV-",
  },
  separate_sequence_per_branch: false,
  // Section 3: Allowed Payment Methods
  allowed_payment_methods: [
    "Cash",
    "Bank Transfer",
    "Cheque",
    "Card",
    "QR Payment",
    "Mobile Payment",
  ],
  payment_method_settings: {
    enable_cash: true,
    enable_bank_transfer: true,
    enable_qr_payment: true,
    enable_cheque: true,
    enable_split_payments: true,
    default_payment_method: "Bank Transfer",
  },
  // Section 4: Field Requirements & Behavior
  field_requirements: {
    require_narration: true,
    require_reference_linking: false,
    require_attachment_upload: false,
    allow_line_level_narration: true,
    enable_multiline_vouchers: true,
    enable_voucher_draft_mode: true,
  },
  attachment_required: {
    payment: true,
    receipt: false,
    journal: false,
    contra: false,
    opening: false,
  },
  // Section 5: Default Narration Templates
  default_narration_templates: {
    payment: [
      "Payment to {{vendor}} for invoice {{invoice_no}}",
      "Utility bill payment - {{utility_type}}",
      "Staff salary payment - {{period}}",
      "Rent payment for {{location}} - {{period}}",
    ],
    receipt: [
      "Advance from {{customer}} received via {{payment_method}}",
      "Cash sales collection - {{date}}",
      "Customer payment received for order {{order_number}}",
      "Refund adjustment for returned items",
    ],
    journal: [
      "Internal adjustment: {{purpose}}",
      "Monthly depreciation adjustment",
      "Inventory adjustment - {{reason}}",
      "Accrual adjustment for {{expense_type}}",
    ],
    contra: [
      "Bank transfer from {{account_a}} to {{account_b}}",
      "Cash deposit to bank account",
      "Inter-bank transfer",
    ],
    opening: [
      "Opening balance entry for account {{account_name}}",
      "Initial setup - {{description}}",
    ],
  },
  allowed_posting_roles: ["Finance Manager", "Accountant", "Admin"],
  created_at: 1710374400000,
  updated_at: 1710374400000,
};

export const mockJournalSettings: JournalSettings = {
  id: "journal-settings-001",
  // 1. Journal Entry Workflow Rules
  enable_draft_journals: true,
  allow_manual_journals: true,
  allow_edits_after_save: false,
  auto_lock_on_post: true,
  allow_reversal_journals: true,
  restrict_partial_entries: true,
  restrict_save_without_narration: true,

  // 2. Journal Entry Numbering Format
  journal_id_prefix: "JE-",
  sequence_reset_annually: true,
  branch_based_sequence: false,

  // 3. Posting Permissions & Role Control
  roles_allowed_to_create: ["Admin", "Finance Manager", "Accountant"],
  roles_allowed_to_post: ["Admin", "Finance Manager"],
  roles_allowed_to_reverse: ["Admin"],
  require_approval_before_posting: false,

  // 4. Audit & Logging Settings
  enable_full_audit_trail: true,
  store_original_entry_snapshot: true,
  log_coa_account_hits: true,
  audit_trail_logging: true,

  // 5. Validation Rules & Safeguards
  enforce_coa_postability: true,
  enforce_account_type_match: true,
  allow_zero_amount_lines: false,
  prevent_duplicate_entries: true,

  // 6. Advanced Features (Optional)
  enable_cost_center_tracking: false,
  enable_departmental_segmenting: false,
  enable_subledger_source_tagging: true,
  default_currency: "BHD",

  // Legacy fields
  require_narration: true,
  minimum_journal_lines: 2,
  created_at: 1710374400000,
  updated_at: 1710374400000,
};

export const mockLedgerSettings: LedgerSettings = {
  id: "ledger-settings-001",

  // 1. Account Visibility Rules
  show_only_postable_accounts: true,
  hide_zero_balance_accounts: false,
  show_archived_accounts: false,
  account_display_format: "[Code] - [Name]",

  // 2. Balance Calculation Settings
  enable_opening_balances: true,
  balance_carry_forward_across_years: true,
  auto_close_nominal_accounts: true,
  currency_handling_mode: "base_currency_only",
  include_draft_journals_in_ledger: false,

  // 3. Filters & User Access Controls
  allow_filtering_by_branch: true,
  allow_filtering_by_department: false,
  role_based_ledger_access: ["Admin", "Finance Manager", "Accountant"],
  role_based_account_group_visibility: {
    "POS Manager": ["Assets", "Revenue"],
    "Finance Manager": [
      "Assets",
      "Liabilities",
      "Equity",
      "Revenue",
      "Expense",
    ],
    Admin: ["Assets", "Liabilities", "Equity", "Revenue", "Expense"],
  },

  // 4. Drill-Down Behavior
  enable_journal_drill_down: true,
  enable_source_module_tags: true,
  enable_view_source_document_link: true,

  // 5. Export & Reporting Controls
  allow_export_to_excel: true,
  allow_export_to_pdf: true,
  allow_export_to_csv: true,
  show_report_footer_summary: true,
  include_applied_filters_in_export: true,

  // Legacy fields for backward compatibility
  enable_branch_filter: true,
  show_reversed_entries_default: false,
  default_date_range: "this_month",
  export_options_allowed: {
    pdf: true,
    excel: true,
    csv: true,
  },
  show_opening_balance_row: true,

  created_at: 1710374400000,
  updated_at: 1710374400000,
};

export const mockUserRoles: UserRole[] = [
  {
    id: "role-001",
    name: "Admin",
    permissions: {
      journals: {
        create: true,
        edit: true,
        delete: true,
        post: true,
        reverse: true,
        view: true,
      },
      vouchers: {
        create: true,
        edit: true,
        delete: true,
        post: true,
        reverse: true,
        view: true,
      },
      coa: {
        create: true,
        edit: true,
        delete: true,
        view: true,
      },
      ledger: {
        view: true,
        export: true,
      },
      configuration: {
        view: true,
        edit: true,
      },
    },
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "role-002",
    name: "Finance Manager",
    permissions: {
      journals: {
        create: true,
        edit: true,
        delete: false,
        post: true,
        reverse: true,
        view: true,
      },
      vouchers: {
        create: true,
        edit: true,
        delete: false,
        post: true,
        reverse: true,
        view: true,
      },
      coa: {
        create: false,
        edit: true,
        delete: false,
        view: true,
      },
      ledger: {
        view: true,
        export: true,
      },
      configuration: {
        view: true,
        edit: false,
      },
    },
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "role-003",
    name: "Accountant",
    permissions: {
      journals: {
        create: true,
        edit: true,
        delete: false,
        post: false,
        reverse: false,
        view: true,
      },
      vouchers: {
        create: true,
        edit: true,
        delete: false,
        post: false,
        reverse: false,
        view: true,
      },
      coa: {
        create: false,
        edit: false,
        delete: false,
        view: true,
      },
      ledger: {
        view: true,
        export: false,
      },
      configuration: {
        view: false,
        edit: false,
      },
    },
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
  {
    id: "role-004",
    name: "Data Entry",
    permissions: {
      journals: {
        create: true,
        edit: false,
        delete: false,
        post: false,
        reverse: false,
        view: true,
      },
      vouchers: {
        create: true,
        edit: false,
        delete: false,
        post: false,
        reverse: false,
        view: true,
      },
      coa: {
        create: false,
        edit: false,
        delete: false,
        view: true,
      },
      ledger: {
        view: false,
        export: false,
      },
      configuration: {
        view: false,
        edit: false,
      },
    },
    created_at: 1710374400000,
    updated_at: 1710374400000,
  },
];

export const mockAccountingConfiguration: AccountingConfiguration = {
  coa_mappings: mockCOAMappings,
  voucher_settings: mockVoucherSettings,
  journal_settings: mockJournalSettings,
  ledger_settings: mockLedgerSettings,
  user_roles: mockUserRoles,
};

// =============================================================================
// VOUCHER TEMPLATE INTERFACES
// =============================================================================

export interface VoucherTemplate {
  id: string;
  // 1. General Information
  template_name: string;
  transaction_code: string;
  voucher_type: "Payment" | "Receipt" | "Journal" | "Contra" | "Opening";
  status: "Active" | "Inactive";

  // 2. COA Mapping
  debit_account_id: string;
  credit_account_id: string;
  optional_account_per_branch: boolean;

  // 3. Allowed Payment Methods
  allowed_payment_methods: string[];
  default_payment_method: string;

  // 4. Default Behavior Options
  require_narration: boolean;
  require_attachment: boolean;
  allow_editing_narration: boolean;
  allow_split_payments: boolean;

  // 5. Narration Template
  narration_template: string;

  // 6. Reference Link Settings
  enable_reference_linking: boolean;
  reference_type: string;

  // 8. System Notes & Metadata
  created_by: string;
  created_date: number;
  last_modified: number;
  last_modified_by: string;
}

// =============================================================================
// MOCK VOUCHER TEMPLATE DATA
// =============================================================================

export const mockVoucherTemplates: VoucherTemplate[] = [
  {
    id: "template-001",
    template_name: "Vendor Payment - Bank",
    transaction_code: "VENDOR_PAYMENT_BANK",
    voucher_type: "Payment",
    status: "Active",
    debit_account_id: "2001", // Accounts Payable
    credit_account_id: "1002", // Bank Account
    optional_account_per_branch: false,
    allowed_payment_methods: ["Bank Transfer"],
    default_payment_method: "Bank Transfer",
    require_narration: true,
    require_attachment: true,
    allow_editing_narration: true,
    allow_split_payments: false,
    narration_template: "Payment to {{vendor}} for invoice {{invoice_no}}",
    enable_reference_linking: true,
    reference_type: "Purchase Invoice",
    created_by: "user-001",
    created_date: 1710374400000,
    last_modified: 1710374400000,
    last_modified_by: "user-001",
  },
  {
    id: "template-002",
    template_name: "Customer Receipt - Cash",
    transaction_code: "CUSTOMER_RECEIPT_CASH",
    voucher_type: "Receipt",
    status: "Active",
    debit_account_id: "1001", // Cash on Hand
    credit_account_id: "4001", // Sales Revenue
    optional_account_per_branch: false,
    allowed_payment_methods: ["Cash"],
    default_payment_method: "Cash",
    require_narration: false,
    require_attachment: false,
    allow_editing_narration: true,
    allow_split_payments: false,
    narration_template:
      "Cash receipt from {{customer}} for order {{order_number}}",
    enable_reference_linking: true,
    reference_type: "Customer Order",
    created_by: "user-002",
    created_date: 1710460800000,
    last_modified: 1710460800000,
    last_modified_by: "user-002",
  },
  {
    id: "template-003",
    template_name: "Salary Payment",
    transaction_code: "SALARY_PAYMENT",
    voucher_type: "Payment",
    status: "Active",
    debit_account_id: "5001", // Salaries Expense
    credit_account_id: "1002", // Bank Account
    optional_account_per_branch: true,
    allowed_payment_methods: ["Bank Transfer"],
    default_payment_method: "Bank Transfer",
    require_narration: true,
    require_attachment: false,
    allow_editing_narration: false,
    allow_split_payments: false,
    narration_template: "Salary payment for {{employee}} - {{period}}",
    enable_reference_linking: true,
    reference_type: "Payslip",
    created_by: "user-003",
    created_date: 1710547200000,
    last_modified: 1710547200000,
    last_modified_by: "user-003",
  },
  {
    id: "template-004",
    template_name: "Utility Bill Payment",
    transaction_code: "UTILITY_PAYMENT",
    voucher_type: "Payment",
    status: "Active",
    debit_account_id: "5301", // Utilities Expense
    credit_account_id: "1002", // Bank Account
    optional_account_per_branch: false,
    allowed_payment_methods: ["Bank Transfer", "Cheque"],
    default_payment_method: "Bank Transfer",
    require_narration: true,
    require_attachment: true,
    allow_editing_narration: true,
    allow_split_payments: false,
    narration_template: "{{utility_type}} bill payment for {{period}}",
    enable_reference_linking: true,
    reference_type: "Expense Report",
    created_by: "user-001",
    created_date: 1710633600000,
    last_modified: 1710633600000,
    last_modified_by: "user-001",
  },
  {
    id: "template-005",
    template_name: "Customer Advance Receipt",
    transaction_code: "CUSTOMER_ADVANCE",
    voucher_type: "Receipt",
    status: "Active",
    debit_account_id: "1002", // Bank Account
    credit_account_id: "2003", // Customer Advances
    optional_account_per_branch: false,
    allowed_payment_methods: ["Bank Transfer", "Cash", "Card"],
    default_payment_method: "Bank Transfer",
    require_narration: true,
    require_attachment: false,
    allow_editing_narration: true,
    allow_split_payments: true,
    narration_template: "Advance payment from {{customer}} for {{purpose}}",
    enable_reference_linking: true,
    reference_type: "Customer Order",
    created_by: "user-002",
    created_date: 1710720000000,
    last_modified: 1710720000000,
    last_modified_by: "user-002",
  },
  {
    id: "template-006",
    template_name: "Depreciation Adjustment",
    transaction_code: "DEPRECIATION_ADJ",
    voucher_type: "Journal",
    status: "Active",
    debit_account_id: "5201", // Depreciation Expense
    credit_account_id: "1401", // Accumulated Depreciation
    optional_account_per_branch: false,
    allowed_payment_methods: [],
    default_payment_method: "",
    require_narration: true,
    require_attachment: false,
    allow_editing_narration: false,
    allow_split_payments: false,
    narration_template:
      "Monthly depreciation adjustment for {{asset_category}}",
    enable_reference_linking: false,
    reference_type: "",
    created_by: "user-003",
    created_date: 1710806400000,
    last_modified: 1710806400000,
    last_modified_by: "user-003",
  },
  {
    id: "template-007",
    template_name: "Petty Cash Reimbursement",
    transaction_code: "PETTY_CASH_REIMB",
    voucher_type: "Payment",
    status: "Inactive",
    debit_account_id: "1001", // Cash on Hand
    credit_account_id: "1002", // Bank Account
    optional_account_per_branch: true,
    allowed_payment_methods: ["Cash"],
    default_payment_method: "Cash",
    require_narration: true,
    require_attachment: true,
    allow_editing_narration: true,
    allow_split_payments: false,
    narration_template: "Petty cash reimbursement for {{branch}} - {{period}}",
    enable_reference_linking: false,
    reference_type: "",
    created_by: "user-001",
    created_date: 1710892800000,
    last_modified: 1710892800000,
    last_modified_by: "user-001",
  },
];

// =============================================================================
// DASHBOARD ANALYTICS INTERFACES
// =============================================================================

export interface DashboardMetrics {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  profitMargin: number;
  cashFlow: number;
  totalAssets: number;
  totalLiabilities: number;
  workingCapital: number;
  currentRatio: number;
  quickRatio: number;
  debtToEquity: number;
  returnOnAssets: number;
  healthScore: number; // 0-100
  period: string;
  date: number;
}

export interface BranchPerformance {
  branchId: string;
  branchName: string;
  revenue: number;
  expenses: number;
  profit: number;
  margin?: number; // optional for backward compatibility
  profitMargin?: number; // used by analytics
  transactions?: number; // optional for backward compatibility
  transactionCount?: number; // used by analytics
  growth?: number; // optional for backward compatibility
  efficiency?: number; // used by analytics
  ranking?: number; // used by analytics
  status: "excellent" | "good" | "warning" | "critical";
  period: string;
}

export interface MonthlyTrend {
  month: string;
  year: number;
  revenue: number;
  expenses: number;
  profit: number;
  cashFlow: number;
  date: number;
}

export interface ExpenseBreakdown {
  category: string;
  amount: number;
  percentage: number;
  trend: number[]; // trend data array for charts
  budget: number;
  variance: number;
  color?: string; // optional color for charts
}

export interface CashFlowData {
  date: number;
  operating: number;
  investing: number;
  financing: number;
  netCashFlow: number;
  cumulativeCash: number;
}

export interface AccountingRatios {
  liquidity: {
    currentRatio: number;
    quickRatio: number;
    cashRatio: number;
  };
  profitability: {
    grossMargin: number;
    netMargin: number;
    returnOnAssets: number;
    returnOnEquity: number;
  };
  efficiency: {
    assetTurnover: number;
    inventoryTurnover: number;
    receivablesTurnover: number;
  };
  leverage: {
    debtToEquity: number;
    debtToAssets: number;
    interestCoverage: number;
  };
}

// ===================================
// ANALYTICS & DASHBOARD TYPES
// ===================================

export interface FinancialRatio {
  name: string;
  value: number;
  benchmark: number;
  status: "excellent" | "good" | "warning" | "critical";
  description: string;
  formula: string;
}

export interface ProfitabilityAnalysis {
  grossProfitMargin: number;
  operatingMargin: number;
  netProfitMargin: number;
  returnOnAssets: number;
  returnOnEquity: number;
  trend: "improving" | "stable" | "declining";
}

export interface LiquidityAnalysis {
  currentRatio: number;
  quickRatio: number;
  cashRatio: number;
  workingCapital: number;
  daysInCash: number;
  status: "healthy" | "adequate" | "concerning" | "critical";
}

export interface CashFlowProjection {
  period: string;
  inflow: number;
  outflow: number;
  netFlow: number;
  cumulativeFlow: number;
  confidence: number;
}

export interface RevenueStream {
  source: string;
  amount: number;
  percentage: number;
  growth: number;
  seasonality: number[];
  predictedGrowth: number;
}

export interface RiskIndicator {
  type: string;
  level: "low" | "medium" | "high";
  score: number;
  description: string;
  recommendation: string;
  impact: number;
  probability: number;
}

export interface TransactionVolume {
  period: string;
  count: number;
  value: number;
  averageValue: number;
  peakHour: string;
  efficiency: number;
}

export interface AccountingHealth {
  reconciliationStatus: string;
  unpostedEntries: number;
  pendingApprovals: number;
  errorRate: number;
  complianceScore: number;
  lastAudit: Date;
}

// Chart Configuration Types
export interface ChartConfiguration {
  id: string;
  title: string;
  type: string;
  series: any[];
  height: number;
  options: any;
}

// =============================================================================
// MOCK DASHBOARD DATA
// =============================================================================

export const mockDashboardMetrics: DashboardMetrics[] = [
  // Current Month (March 2024)
  {
    totalRevenue: 144400,
    totalExpenses: 58000,
    netProfit: 86400,
    profitMargin: 59.8,
    cashFlow: 78200,
    totalAssets: 229000,
    totalLiabilities: 18000,
    workingCapital: 181000,
    currentRatio: 9.61,
    quickRatio: 8.94,
    debtToEquity: 0.085,
    returnOnAssets: 37.7,
    healthScore: 92,
    period: "March 2024",
    date: 1710374400000,
  },
  // Previous Month (February 2024)
  {
    totalRevenue: 138200,
    totalExpenses: 55800,
    netProfit: 82400,
    profitMargin: 59.6,
    cashFlow: 74500,
    totalAssets: 225000,
    totalLiabilities: 19200,
    workingCapital: 178000,
    currentRatio: 9.27,
    quickRatio: 8.65,
    debtToEquity: 0.093,
    returnOnAssets: 36.6,
    healthScore: 89,
    period: "February 2024",
    date: 1707696000000,
  },
  // January 2024
  {
    totalRevenue: 132800,
    totalExpenses: 53200,
    netProfit: 79600,
    profitMargin: 59.9,
    cashFlow: 71200,
    totalAssets: 221000,
    totalLiabilities: 20500,
    workingCapital: 175000,
    currentRatio: 8.98,
    quickRatio: 8.41,
    debtToEquity: 0.102,
    returnOnAssets: 36.0,
    healthScore: 87,
    period: "January 2024",
    date: 1704067200000,
  },
];

export const mockBranchPerformance: BranchPerformance[] = [
  {
    branchId: "branch-001",
    branchName: "Sitra Branch",
    revenue: 28500,
    expenses: 11400,
    profit: 17100,
    margin: 60.0,
    transactions: 1250,
    growth: 12.5,
    status: "excellent",
    period: "March 2024",
  },
  {
    branchId: "branch-002",
    branchName: "Manama Branch",
    revenue: 35200,
    expenses: 14080,
    profit: 21120,
    margin: 60.0,
    transactions: 1680,
    growth: 18.3,
    status: "excellent",
    period: "March 2024",
  },
  {
    branchId: "branch-003",
    branchName: "Riffa Branch",
    revenue: 22800,
    expenses: 9120,
    profit: 13680,
    margin: 60.0,
    transactions: 980,
    growth: 8.7,
    status: "good",
    period: "March 2024",
  },
  {
    branchId: "branch-004",
    branchName: "Muharraq Branch",
    revenue: 31200,
    expenses: 12480,
    profit: 18720,
    margin: 60.0,
    transactions: 1420,
    growth: 15.2,
    status: "excellent",
    period: "March 2024",
  },
  {
    branchId: "branch-005",
    branchName: "Isa Town Branch",
    revenue: 26700,
    expenses: 10680,
    profit: 16020,
    margin: 60.0,
    transactions: 1150,
    growth: 6.8,
    status: "good",
    period: "March 2024",
  },
];

export const mockMonthlyTrends: MonthlyTrend[] = [
  {
    month: "Sep",
    year: 2023,
    revenue: 118500,
    expenses: 47400,
    profit: 71100,
    cashFlow: 64200,
    date: 1693526400000,
  },
  {
    month: "Oct",
    year: 2023,
    revenue: 122800,
    expenses: 49120,
    profit: 73680,
    cashFlow: 66500,
    date: 1696118400000,
  },
  {
    month: "Nov",
    year: 2023,
    revenue: 126200,
    expenses: 50480,
    profit: 75720,
    cashFlow: 68300,
    date: 1698796800000,
  },
  {
    month: "Dec",
    year: 2023,
    revenue: 129600,
    expenses: 51840,
    profit: 77760,
    cashFlow: 70100,
    date: 1701388800000,
  },
  {
    month: "Jan",
    year: 2024,
    revenue: 132800,
    expenses: 53200,
    profit: 79600,
    cashFlow: 71200,
    date: 1704067200000,
  },
  {
    month: "Feb",
    year: 2024,
    revenue: 138200,
    expenses: 55800,
    profit: 82400,
    cashFlow: 74500,
    date: 1707696000000,
  },
  {
    month: "Mar",
    year: 2024,
    revenue: 144400,
    expenses: 58000,
    profit: 86400,
    cashFlow: 78200,
    date: 1710374400000,
  },
];

export const mockExpenseBreakdown: ExpenseBreakdown[] = [
  {
    category: "Salaries & Wages",
    amount: 25000,
    percentage: 43.1,
    trend: [22000, 23500, 24200, 24800, 25000, 25200, 25500],
    budget: 24000,
    variance: 1000,
    color: "#3B82F6",
  },
  {
    category: "Cost of Goods Sold",
    amount: 18500,
    percentage: 31.9,
    trend: [17800, 18000, 18200, 18300, 18500, 18600, 18700],
    budget: 19000,
    variance: -500,
    color: "#10B981",
  },
  {
    category: "Utilities & Rent",
    amount: 8200,
    percentage: 14.1,
    trend: [8000, 8050, 8100, 8150, 8200, 8220, 8250],
    budget: 8000,
    variance: 200,
    color: "#F59E0B",
  },
  {
    category: "Marketing & Advertising",
    amount: 3800,
    percentage: 6.6,
    trend: [4000, 3950, 3900, 3850, 3800, 3780, 3750],
    budget: 4000,
    variance: -200,
    color: "#EF4444",
  },
  {
    category: "Administrative",
    amount: 2500,
    percentage: 4.3,
    trend: [2450, 2470, 2480, 2490, 2500, 2510, 2520],
    budget: 2500,
    variance: 0,
    color: "#8B5CF6",
  },
];

export const mockCashFlowData: CashFlowData[] = [
  {
    date: 1693526400000, // Sep 2023
    operating: 64200,
    investing: -5000,
    financing: -2000,
    netCashFlow: 57200,
    cumulativeCash: 145000,
  },
  {
    date: 1696118400000, // Oct 2023
    operating: 66500,
    investing: -3000,
    financing: -1500,
    netCashFlow: 62000,
    cumulativeCash: 150000,
  },
  {
    date: 1698796800000, // Nov 2023
    operating: 68300,
    investing: -2000,
    financing: -1000,
    netCashFlow: 65300,
    cumulativeCash: 155000,
  },
  {
    date: 1701388800000, // Dec 2023
    operating: 70100,
    investing: -4000,
    financing: -2500,
    netCashFlow: 63600,
    cumulativeCash: 158000,
  },
  {
    date: 1704067200000, // Jan 2024
    operating: 71200,
    investing: -1500,
    financing: -1000,
    netCashFlow: 68700,
    cumulativeCash: 162000,
  },
  {
    date: 1707696000000, // Feb 2024
    operating: 74500,
    investing: -2500,
    financing: -1200,
    netCashFlow: 70800,
    cumulativeCash: 168000,
  },
  {
    date: 1710374400000, // Mar 2024
    operating: 78200,
    investing: -3500,
    financing: -1800,
    netCashFlow: 72900,
    cumulativeCash: 173000,
  },
];

export const mockAccountingRatios: AccountingRatios = {
  liquidity: {
    currentRatio: 9.61,
    quickRatio: 8.94,
    cashRatio: 7.83,
  },
  profitability: {
    grossMargin: 68.5,
    netMargin: 59.8,
    returnOnAssets: 37.7,
    returnOnEquity: 40.9,
  },
  efficiency: {
    assetTurnover: 0.63,
    inventoryTurnover: 12.4,
    receivablesTurnover: 18.2,
  },
  leverage: {
    debtToEquity: 0.085,
    debtToAssets: 0.079,
    interestCoverage: 45.2,
  },
};

// =============================================================================
// DASHBOARD EXPORT
// =============================================================================

export const dashboardData = {
  metrics: mockDashboardMetrics,
  branchPerformance: mockBranchPerformance,
  monthlyTrends: mockMonthlyTrends,
  expenseBreakdown: mockExpenseBreakdown,
  cashFlowData: mockCashFlowData,
  accountingRatios: mockAccountingRatios,
};
